/**
 * useEagerUploads — uploads each staged file and frame as soon as it is
 * attached, so pressing Send only posts the message. What the server holds
 * goes into the session's upload ledger, which the send path already reads:
 * an item uploaded here is never uploaded again, and an item whose eager
 * upload failed simply uploads with the send, as before.
 *
 * Removing an item stops its upload, or deletes the server's copy once it
 * finished, so the user's unsent-upload quota frees at once; a failed delete
 * is left to the server's 24 h sweep — as is a file the server stored just as
 * its upload was aborted, which nothing on this side learns of.
 */
import { useCallback, useEffect, useMemo, useState, type RefObject } from 'react';

import {
  classifySendError,
  deleteThreadFile,
  FILE_UPLOAD_CONCURRENCY,
  MessageSendError,
  uploadThreadFile,
  type SendFailureKind,
  type UploadedFile,
} from '@discovr/features';
import type { ThreadFileView } from '@discovr/types';

import type { StagedFile } from '../../../../../lib/media/pickFiles';
import { fileDraftFor, type SendAttempt, type StagedMomentState, type UploadLedger } from './sendDraft';

/** A refusal of the file itself: resending the same file can't succeed. */
const FILE_REJECTED_KIND: SendFailureKind = 'file_rejected';

/** What identifies an eager upload: a staged file's id, or the staged moment whose frame it carries. */
type UploadKey = string | StagedMomentState;

/** One eager upload: how to stop it, and when it has settled. */
interface Upload {
  controller: AbortController;
  done: Promise<void>;
}

export interface EagerUploads {
  /** Starts compressing + uploading a staged file now; the ledger gets its id when the server holds it. */
  startFile(file: StagedFile): void;
  /** Starts uploading a staged moment's frame now. */
  startFrame(staged: StagedMomentState): void;
  /**
   * Stops a file's upload, or deletes the server's copy once it finished. An abort
   * that races an upload the server already stored leaves that file to its 24 h sweep.
   */
  discardFile(id: string): void;
  /** Same for a frame that was replaced or cleared. */
  discardFrame(staged: StagedMomentState): void;
  /** Resolves once every eager upload of the attempt's files and frame has settled — success or not. */
  settled(attempt: SendAttempt): Promise<void>;
  /**
   * Holds back every upload except the attempt's own until the returned
   * function is called: while a send uploads what it carries, one user's
   * uploads share the budget the server allows (FILE_UPLOAD_CONCURRENCY), so
   * a file picked mid-send waits instead of tipping it over. The attempt's
   * items are never held, so the send can always settle them.
   */
  holdOthers(attempt: SendAttempt): () => void;
  /**
   * Stops every upload still in transfer and forgets them, except those a send
   * in flight carries — that send still needs them. Deletes nothing itself: for
   * a session moving to another entry, where a delete would name the new entry.
   * An upload the server finishes anyway is deleted under the entry it started for.
   */
  abortAll(inFlight?: ReadonlySet<SendAttempt>): void;
}

/** No send in flight: `abortAll` stops everything. */
const NO_SENDS_IN_FLIGHT: ReadonlySet<SendAttempt> = new Set();

/** Best-effort delete of uploads no send will carry, under the entry they were uploaded to. */
export function deleteServerCopies(entryId: string, fileIds: string[]): void {
  fileIds.forEach((fileId) => {
    void deleteThreadFile(entryId, fileId).catch(logDeleteFailure);
  });
}

export interface UseEagerUploadsArgs {
  entryId: string;
  ledger: UploadLedger;
  maxFileBytes: number;
  onProgress(id: string, fraction: number): void;
  /** The server refused the file itself (type, size, dimensions): the chip is marked and the user told now. */
  onRejected(id: string, err: unknown): void;
  /** A file's upload ended — taken by the server, or failed (refused or not) — so its chip can drop the upload's ring. */
  onSettled?(id: string): void;
  /** Sends in flight, read when the hook unmounts: the uploads they carry outlive it, since the sends still need them. */
  inFlightRef?: RefObject<ReadonlySet<SendAttempt>>;
}

/**
 * Whether a failed upload was the server refusing the file itself — by the
 * send path's own classification, so the two never disagree. An image still
 * over the limit after compression is refused before upload, as a
 * MessageSendError. A frame never gets here: it is capped when it is baked.
 */
function isRejection(err: unknown): boolean {
  if (err instanceof MessageSendError) return err.kind === FILE_REJECTED_KIND;
  return classifySendError(err) === FILE_REJECTED_KIND;
}

function toUploaded(view: ThreadFileView): UploadedFile {
  return { fileId: view.id, width: view.width, height: view.height };
}

function logDeleteFailure(err: unknown): void {
  console.warn('[useEagerUploads] removing an unsent upload failed — the server sweep will remove it', err);
}

function logFailureHandlerError(err: unknown): void {
  console.warn('[useEagerUploads] handling a failed upload threw', err);
}

function ignoreFailure(): void {
  // A frame that failed uploads again with the send, which reports its own error.
}

/** The uploads a send carries: its files and its frame. */
function attemptKeys(attempt: SendAttempt): UploadKey[] {
  const keys: UploadKey[] = attempt.files.map((file) => file.id);
  if (attempt.staged) keys.push(attempt.staged);
  return keys;
}

interface Hold {
  allowed: ReadonlySet<UploadKey>;
  lifted: Promise<void>;
  lift: () => void;
}

/** Holds back uploads outside a send in flight until the send is done (see `EagerUploads.holdOthers`). */
class UploadGate {
  private hold: Hold | null = null;

  /** Resolves once `key` may start: at once unless a hold that doesn't cover it is in place. */
  async pass(key: UploadKey): Promise<void> {
    while (this.hold && !this.hold.allowed.has(key)) {
      await this.hold.lifted;
    }
  }

  /** Holds every key but `allowed`; the returned function lifts it (once). */
  close(allowed: UploadKey[]): () => void {
    this.hold?.lift();
    let lift: () => void = () => undefined;
    const lifted = new Promise<void>((resolve) => {
      lift = resolve;
    });
    const hold: Hold = { allowed: new Set(allowed), lifted, lift };
    this.hold = hold;
    return () => {
      if (this.hold === hold) this.hold = null;
      hold.lift();
    };
  }
}

/** The FILE_UPLOAD_CONCURRENCY transfer slots: an upload takes one to start and hands it on when it settles. */
class UploadSlots {
  private active = 0;
  private readonly waiting: Array<() => void> = [];

  acquire(): Promise<void> {
    if (this.active < FILE_UPLOAD_CONCURRENCY) {
      this.active += 1;
      return Promise.resolve();
    }
    return new Promise((resolve) => this.waiting.push(resolve));
  }

  /** Hands the slot to the next waiting upload, or frees it. */
  release(): void {
    const next = this.waiting.shift();
    if (next) {
      next();
      return;
    }
    this.active -= 1;
  }
}

export function useEagerUploads({ entryId, ledger, maxFileBytes, onProgress, onRejected, onSettled, inFlightRef }: UseEagerUploadsArgs): EagerUploads {
  // Stable instances, created once and never replaced through setState (the lazy-useState idiom, as
  // TanStack's `useState(() => new QueryClient())`): mutating them never needs a re-render.
  const [uploads] = useState(() => new Map<UploadKey, Upload>());
  const [slots] = useState(() => new UploadSlots());
  const [gate] = useState(() => new UploadGate());

  const run = useCallback(
    (key: UploadKey, upload: (signal: AbortSignal) => Promise<void>, onFailure: (err: unknown) => void) => {
      const controller = new AbortController();
      /** `done` must always resolve — settled() waits on it — so a throwing handler is logged, not propagated. */
      function reportFailure(err: unknown): void {
        try {
          onFailure(err);
        } catch (handlerError) {
          logFailureHandlerError(handlerError);
        }
      }
      /** Only this run's own entry: the same key may have been started again since. */
      function forget(): void {
        if (uploads.get(key)?.controller === controller) uploads.delete(key);
      }
      async function go(): Promise<void> {
        await gate.pass(key);
        await slots.acquire();
        try {
          if (controller.signal.aborted) return;
          await upload(controller.signal);
        } catch (err) {
          if (!controller.signal.aborted) reportFailure(err);
        } finally {
          slots.release();
          forget();
        }
      }
      uploads.set(key, { controller, done: go() });
    },
    [gate, slots, uploads],
  );

  const deleteServerCopy = useCallback(
    (fileId: string | undefined) => {
      if (!fileId) return;
      void deleteThreadFile(entryId, fileId).catch(logDeleteFailure);
    },
    [entryId],
  );

  /** An upload that finished after its item was removed is the server's orphan: delete it rather than record it. */
  const keepOrDelete = useCallback(
    (signal: AbortSignal, fileId: string, record: () => void) => {
      if (signal.aborted) {
        deleteServerCopy(fileId);
        return;
      }
      record();
    },
    [deleteServerCopy],
  );

  const startFile = useCallback(
    (staged: StagedFile) => {
      if (uploads.has(staged.id) || ledger.files.has(staged.id)) return;
      run(
        staged.id,
        async (signal) => {
          const draft = await fileDraftFor(staged, ledger, maxFileBytes);
          const view = await uploadThreadFile({
            entryId,
            file: draft.file,
            name: draft.name,
            signal,
            onProgress: (fraction) => onProgress(staged.id, fraction),
          });
          keepOrDelete(signal, view.id, () => ledger.files.set(staged.id, view.id));
          onSettled?.(staged.id);
        },
        (err) => {
          try {
            if (isRejection(err)) onRejected(staged.id, err);
          } finally {
            onSettled?.(staged.id);
          }
        },
      );
    },
    [run, keepOrDelete, uploads, ledger, maxFileBytes, entryId, onProgress, onRejected, onSettled],
  );

  const startFrame = useCallback(
    (staged: StagedMomentState) => {
      const frame = staged.moment.frame;
      if (!frame) return;
      if (uploads.has(staged) || ledger.frames.has(staged)) return;
      run(
        staged,
        async (signal) => {
          const view = await uploadThreadFile({ entryId, file: frame.image.blob, name: frame.name, signal });
          keepOrDelete(signal, view.id, () => ledger.frames.set(staged, toUploaded(view)));
        },
        ignoreFailure,
      );
    },
    [run, keepOrDelete, uploads, ledger, entryId],
  );

  /** Stops an upload still in transfer and drops it, so the same item can start again at once. */
  const abortUpload = useCallback(
    (key: UploadKey) => {
      uploads.get(key)?.controller.abort();
      uploads.delete(key);
    },
    [uploads],
  );

  const discardFile = useCallback(
    (id: string) => {
      abortUpload(id);
      deleteServerCopy(ledger.files.get(id));
      ledger.files.delete(id);
    },
    [abortUpload, ledger, deleteServerCopy],
  );

  const discardFrame = useCallback(
    (staged: StagedMomentState) => {
      abortUpload(staged);
      deleteServerCopy(ledger.frames.get(staged)?.fileId);
      ledger.frames.delete(staged);
    },
    [abortUpload, ledger, deleteServerCopy],
  );

  const settled = useCallback(
    async (attempt: SendAttempt): Promise<void> => {
      await Promise.all(attemptKeys(attempt).map((key) => uploads.get(key)?.done));
    },
    [uploads],
  );

  const holdOthers = useCallback((attempt: SendAttempt) => gate.close(attemptKeys(attempt)), [gate]);

  const abortAll = useCallback(
    (inFlight: ReadonlySet<SendAttempt> = NO_SENDS_IN_FLIGHT) => {
      const carried = new Set([...inFlight].flatMap(attemptKeys));
      uploads.forEach((upload, key) => {
        if (carried.has(key)) return;
        upload.controller.abort();
        uploads.delete(key);
      });
    },
    [uploads],
  );

  useEffect(() => () => abortAll(inFlightRef?.current), [abortAll, inFlightRef]);

  return useMemo(
    () => ({ startFile, startFrame, discardFile, discardFrame, settled, holdOthers, abortAll }),
    [startFile, startFrame, discardFile, discardFrame, settled, holdOthers, abortAll],
  );
}
